// CropModal works against any media-shaped object that exposes the
// fields it actually reads (id + media_type + media_url). re-using
// it from the review queue meant accepting ReviewItem too without
// dragging the full MediaItem type in
type CropTarget = {
    id: string;
    media_type: string;
    media_url: string;
};
import {
    Button,
    Modal,
} from "@mrflark/ui";
import {
    useMutation,
} from "@tanstack/react-query";
import {
    RotateCcw,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";
import {
    useCallback,
    useEffect,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";
import {
    splClient,
} from "../../api/client.ts";

type CropModalProps = {
    isOpen: boolean;
    onClose: () => void;
    item: CropTarget | null;
    onCropComplete?: () => void;
};

type CropValues = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};

const DEFAULT_STEP = 10;
const FINE_STEP = 1;

export const CropModal = ({
    isOpen,
    onClose,
    item,
    onCropComplete,
}: CropModalProps): ReactElement => {
    const [crop, setCrop] = useState<CropValues>({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    });
    const [imageDimensions, setImageDimensions] = useState<
        { width: number; height: number; } | null
    >(null);

    useEffect(() => {
        if (isOpen) {
            setCrop({
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            });
            setImageDimensions(null);
        }
    }, [isOpen, item?.id]);

    const cropMutation = useMutation({
        mutationFn: () => splClient.cropMedia(item?.id ?? "", crop),
        onSuccess: (result) => {
            toast.success(
                `Cropped to ${result.new_dimensions.width}x${result.new_dimensions.height}`,
            );
            onCropComplete?.();
            onClose();
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : "Crop failed");
        },
    });

    const adjustCrop = useCallback((edge: keyof CropValues, delta: number) => {
        setCrop((prev) => {
            const newValue = Math.max(0, prev[edge] + delta);
            if (!imageDimensions) {
                return {
                    ...prev,
                    [edge]: newValue,
                };
            }

            const horizontal = edge === "left" || edge === "right";
            const maxDim = horizontal
                ? imageDimensions.width
                : imageDimensions.height;
            const otherEdge = horizontal
                ? (edge === "left" ? prev.right : prev.left)
                : (edge === "top" ? prev.bottom : prev.top);

            // ensure at least 1px remains
            const clamped = Math.min(newValue, maxDim - otherEdge - 1);
            return {
                ...prev,
                [edge]: Math.max(0, clamped),
            };
        });
    }, [imageDimensions]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent): void => {
            const target = event.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
                return;
            }

            const step = event.shiftKey ? FINE_STEP : DEFAULT_STEP;

            switch (event.key.toLowerCase()) {
                case "i":
                    event.preventDefault();
                    adjustCrop("top", step);
                    break;
                case "k":
                    event.preventDefault();
                    adjustCrop("bottom", step);
                    break;
                case "j":
                    event.preventDefault();
                    adjustCrop("left", step);
                    break;
                case "l":
                    event.preventDefault();
                    adjustCrop("right", step);
                    break;
            }
        };

        globalThis.addEventListener("keydown", handleKeyDown);
        return () => globalThis.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, adjustCrop]);

    const handleImageLoad = (event: Event): void => {
        const img = event.currentTarget as HTMLImageElement;
        setImageDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
        });
    };

    const newWidth = imageDimensions
        ? imageDimensions.width - crop.left - crop.right
        : null;
    const newHeight = imageDimensions
        ? imageDimensions.height - crop.top - crop.bottom
        : null;
    const hasCrop = crop.top > 0 || crop.right > 0 || crop.bottom > 0 ||
        crop.left > 0;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Crop Image" size="lg">
            {!item || item.media_type !== "image"
                ? (
                    <p className="text-text-secondary">
                        Only images can be cropped.
                    </p>
                )
                : (
                    <div className="space-y-4">
                        <div className="relative w-fit mx-auto">
                            <img
                                src={item.media_url}
                                alt="Crop preview"
                                className="max-h-[50vh] max-w-full mx-auto rounded-lg"
                                onLoad={handleImageLoad}
                            />
                            {hasCrop && imageDimensions && (
                                <>
                                    {crop.top > 0 && (
                                        <div
                                            className="absolute top-0 left-0 right-0 bg-red-500/30 pointer-events-none"
                                            style={{
                                                height: `${
                                                    (crop.top /
                                                        imageDimensions
                                                            .height) * 100
                                                }%`,
                                            }}
                                        />
                                    )}
                                    {crop.bottom > 0 && (
                                        <div
                                            className="absolute bottom-0 left-0 right-0 bg-red-500/30 pointer-events-none"
                                            style={{
                                                height: `${
                                                    (crop.bottom /
                                                        imageDimensions
                                                            .height) * 100
                                                }%`,
                                            }}
                                        />
                                    )}
                                    {crop.left > 0 && (
                                        <div
                                            className="absolute top-0 left-0 bottom-0 bg-red-500/30 pointer-events-none"
                                            style={{
                                                width: `${
                                                    (crop.left /
                                                        imageDimensions.width) *
                                                    100
                                                }%`,
                                            }}
                                        />
                                    )}
                                    {crop.right > 0 && (
                                        <div
                                            className="absolute top-0 right-0 bottom-0 bg-red-500/30 pointer-events-none"
                                            style={{
                                                width: `${
                                                    (crop.right /
                                                        imageDimensions.width) *
                                                    100
                                                }%`,
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                            <div className="flex items-center gap-4">
                                <span className="text-text-secondary">
                                    {imageDimensions
                                        ? `${imageDimensions.width}x${imageDimensions.height}`
                                        : "Loading..."}
                                    {hasCrop && newWidth && newHeight && (
                                        <span className="text-accent ml-1">
                                            → {newWidth}x{newHeight}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-text-muted">
                                <span>
                                    <kbd className="px-1 py-0.5 bg-bg-tertiary border border-border rounded font-mono">
                                        I
                                    </kbd>{" "}
                                    top
                                </span>
                                <span>
                                    <kbd className="px-1 py-0.5 bg-bg-tertiary border border-border rounded font-mono">
                                        K
                                    </kbd>{" "}
                                    bottom
                                </span>
                                <span>
                                    <kbd className="px-1 py-0.5 bg-bg-tertiary border border-border rounded font-mono">
                                        J
                                    </kbd>{" "}
                                    left
                                </span>
                                <span>
                                    <kbd className="px-1 py-0.5 bg-bg-tertiary border border-border rounded font-mono">
                                        L
                                    </kbd>{" "}
                                    right
                                </span>
                                <span className="text-text-muted">
                                    +Shift = 1px
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-4 gap-2 text-center text-xs">
                            <div>
                                <span className="text-text-muted">Top</span>
                                <div className="text-text-primary font-mono">
                                    {crop.top}px
                                </div>
                            </div>
                            <div>
                                <span className="text-text-muted">Right</span>
                                <div className="text-text-primary font-mono">
                                    {crop.right}px
                                </div>
                            </div>
                            <div>
                                <span className="text-text-muted">Bottom</span>
                                <div className="text-text-primary font-mono">
                                    {crop.bottom}px
                                </div>
                            </div>
                            <div>
                                <span className="text-text-muted">Left</span>
                                <div className="text-text-primary font-mono">
                                    {crop.left}px
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    setCrop({
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        left: 0,
                                    })}
                                disabled={!hasCrop || cropMutation.isPending}
                            >
                                <RotateCcw size={14} />
                                Reset
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={cropMutation.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => cropMutation.mutate()}
                                    disabled={!hasCrop ||
                                        cropMutation.isPending}
                                >
                                    {cropMutation.isPending
                                        ? "Cropping..."
                                        : "Apply Crop"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
        </Modal>
    );
};
